import { createApi } from '@reduxjs/toolkit/query/react'
import { PaginatedResponseProject, Project, ReorderPage, TenantConfigResponse } from '../types'
import { baseQuery } from './BaseApi'

// Define a service using a base URL and expected endpoints
export const projectApi = createApi({
  reducerPath: 'projectApi',
  baseQuery: baseQuery,
  tagTypes: ['Project','Tenant'],
  endpoints: (builder) => ({
    getProjects: builder.query<PaginatedResponseProject, string>({
      query: () => `v1/projects/`,
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Project' as const, id })),
              { type: 'Project', id: 'LIST' },
            ]
          : [{ type: 'Project', id: 'LIST' }],
      
    }),
    getProject: builder.query<Project, any>({
      query: (id) => `v1/projects/${id}`,
      providesTags: (result, error, id) => [{ type: 'Project', id }],
      
    }),
    deleteProject: builder.mutation<boolean, string>({
      query(id) {
        return {
          url: `v1/projects/${id}`,
          method: 'DELETE'
        }
      },
      invalidatesTags: (result, error, id) => [{ type: 'Project', id }],
      
    }),
    reorderPage:builder.mutation<ReorderPage[], any>({
      query: ({id, body}) => ({
				url:`v1/projects/${id}/reorderPageProject`,
				method:'PUT',
				body,
			}),
      async onQueryStarted( {projectId, body }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					projectApi.util.updateQueryData('getProjects', projectId, (draft) => {
						Object.assign(draft, body);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(projectApi.util.invalidateTags([{ type: 'Project', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
    }),
    configureProject:builder.mutation<Project, any>({
      query: ({projectId, body}) => ({
        url:`v1/projects/${projectId}`,
        method:'PUT',
        body
      }),
      async onQueryStarted( {projectId, body }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					projectApi.util.updateQueryData('getProjects', projectId, (draft) => {
						Object.assign(draft, body);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(projectApi.util.invalidateTags([{ type: 'Project', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
    }),
    uploadImage:builder.mutation({
      query:({body}) => ({
        url:`image-data/upload`,
        method:'POST',
        body:body
      })
    }),
    getTenantConfig:builder.query<TenantConfigResponse, string>({
      query:(tenantName) => ({
        url:`v1/config/login-page/${tenantName}`
      }),
      providesTags: (result, error, tenantName) => [{ type: 'Tenant', tenantName }],
    }),
    editTenantConfig:builder.mutation<TenantConfigResponse, any>({
      query:({id, body}) => ({
        url:`v1/config/login-pages/${id}`,
        method:'PUT',
        body
      }),
     invalidatesTags: (result, error, id) => [{ type: 'Tenant', id }],
    })
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useEditTenantConfigMutation, useGetProjectsQuery, useGetProjectQuery, useReorderPageMutation, useConfigureProjectMutation, useUploadImageMutation, useGetTenantConfigQuery } = projectApi